// ============================================================================
// messageSplit.js — hosszú üzenet feldarabolása küldhető részekre.
//
// A szerver üzenetenként legfeljebb 2000 karaktert (és 8000 UTF-8 bájtot)
// fogad el. Enélkül a modul nélkül a hosszabb üzenet egyszerűen elutasításra
// került, és a felhasználó egy általános "nem sikerült elküldeni" piros
// szöveget kapott - anélkül, hogy megtudta volna, MIÉRT, vagy hogy a
// begépelt szövegével mi lesz.
//
// A darabolás igyekszik ÉRTELMES helyen vágni, ebben a sorrendben:
//   1. bekezdéshatár (üres sor),
//   2. sortörés,
//   3. mondathatár (. ! ? 。 ！ ？ után),
//   4. szóköz,
//   5. ha semmi más nem megy (pl. hosszú japán szöveg szóközök nélkül),
//      akkor karakterhatáron - de SOHA nem a közepén egy olyan
//      karakternek, ami két kódegységből áll (emodzsi, egyes kandzsik).
//
// A bájtkorlát külön figyelmet igényel: egy japán karakter 3 bájt, egy
// emodzsi 4 - így 2000 karakter is túllépheti a 8000 bájtot.
// ============================================================================

// A szerver korlátai (functions/src/lib/validation.js). Ha ott változnak,
// ITT IS módosítani kell - a kliensaudit ezt az egyezést ellenőrzi.
export const MAX_TEXT_CODEPOINTS = 2000;
// Fórumbejegyzés (2026-10-09, tagi döntés): egy témába írt hozzászólás legfeljebb 5000 karakter -
// eddig NEM darabolunk; a szerver (functions/src/lib/validation.js) ugyanezt engedi.
export const MAX_FORUM_TEXT_CODEPOINTS = 5000;
export const MAX_FORUM_TEXT_UTF8_BYTES = 20000;
/** A darabolás korlátai: fórumbejegyzésnél a nagyobbak. */
export function darabolasiKorlat(forum) {
  return forum ? { maxChars: MAX_FORUM_TEXT_CODEPOINTS, maxBytes: MAX_FORUM_TEXT_UTF8_BYTES } : {};
}
export const MAX_TEXT_UTF8_BYTES = 8000;

// Biztonsági tartalék: a szerver a "#" előtagot levágja, a darabjelölés
// pedig hozzáad néhány karaktert.
const SAFETY_MARGIN = 20;

const encoder = typeof TextEncoder !== "undefined" ? new TextEncoder() : null;

/** Egy szöveg hossza UTF-8 bájtban. */
export function byteLength(text) {
  if (encoder) return encoder.encode(text).length;
  return unescape(encodeURIComponent(text)).length;
}

/** Kódpontok (nem kódegységek!) száma - az emodzsi is egynek számít. */
export function codePointLength(text) {
  return [...text].length;
}

/** Belefér-e egy darabba? */
function befer(text, maxChars, maxBytes) {
  return codePointLength(text) <= maxChars && byteLength(text) <= maxBytes;
}

/**
 * A legnagyobb olyan előtag hossza (kódpontban), ami még belefér.
 * Bináris keresés: a bájthossz miatt nem számolható ki közvetlenül.
 */
function maxBeferoHossz(chars, maxChars, maxBytes) {
  let lo = 0;
  let hi = Math.min(chars.length, maxChars);
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (byteLength(chars.slice(0, mid).join("")) <= maxBytes) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/**
 * Hosszú szöveg feldarabolása.
 *
 * @param {string} text
 * @param {{maxChars?: number, maxBytes?: number}} [opts] csak a teszteknek
 * @returns {string[]} a darabok, sorrendben. Rövid szövegnél egyetlen elem.
 */
export function splitMessage(text, opts = {}) {
  const maxChars = (opts.maxChars || MAX_TEXT_CODEPOINTS) - SAFETY_MARGIN;
  const maxBytes = (opts.maxBytes || MAX_TEXT_UTF8_BYTES) - SAFETY_MARGIN * 4;

  const teljes = String(text || "");
  if (teljes.trim() === "") return [];
  if (befer(teljes, maxChars, maxBytes)) return [teljes];

  const darabok = [];
  let maradek = teljes;

  while (maradek.length > 0) {
    if (befer(maradek, maxChars, maxBytes)) {
      darabok.push(maradek);
      break;
    }

    const chars = [...maradek];
    const hatar = maxBeferoHossz(chars, maxChars, maxBytes);
    if (hatar <= 0) break; // elvi eset: egyetlen karakter sem fér be

    const jelolt = chars.slice(0, hatar).join("");

    // Értelmes vágópont keresése a jelölt VÉGÉHEZ közel. Csak akkor
    // fogadjuk el, ha nem vág le túl sokat (legalább a fele megmarad) -
    // különben sok apró darab keletkezne.
    const minimum = Math.floor(jelolt.length * 0.5);
    let vagas = -1;
    for (const minta of [/\n\s*\n(?![\s\S]*\n\s*\n)/, /\n(?![\s\S]*\n)/, /[.!?。！？]\s(?![\s\S]*[.!?。！？]\s)/, /\s(?!\S*\s)/]) {
      const m = jelolt.match(minta);
      if (m && m.index >= minimum) {
        vagas = m.index + m[0].length;
        break;
      }
    }
    if (vagas <= 0) vagas = jelolt.length; // kemény vágás karakterhatáron

    const darab = maradek.slice(0, vagas);
    darabok.push(darab.trimEnd());
    maradek = maradek.slice(vagas).replace(/^\s+/, "");
  }

  return darabok.filter((d) => d.trim() !== "");
}

/**
 * Darabjelölés: "(1/3)" a darab VÉGÉN, hogy a több részben érkező üzenet
 * olvasható legyen. Egyetlen darabnál nincs jelölés.
 *
 * A jelölés a fordításba is belekerül, de számjegyekből áll, tehát a
 * modell érintetlenül hagyja.
 */
export function labelPart(darab, index, osszes) {
  if (osszes <= 1) return darab;
  return `${darab}\n(${index + 1}/${osszes})`;
}
